import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  SITE_FOOTER_CSS,
  footerColumns,
  footerGridColumns,
  footerSocialSvg,
  footerSocials,
} from '../constants/siteFooterWidget';

function stop(event) {
  event.preventDefault();
}

/**
 * Palco do rodapé. A largura da página preenche o canvas, sem usar a largura da janela.
 */
export default function RenderSiteFooter({ content = {} }) {
  const slotRef = useRef(null);
  const [stageBleed, setStageBleed] = useState(null);
  const widthMode = content.widthMode === 'boxed' ? 'boxed' : 'full';
  const layout = ['columns', 'centered', 'compact'].includes(content.layout) ? content.layout : 'columns';
  useLayoutEffect(() => {
    if (widthMode !== 'full') {
      setStageBleed(null);
      return undefined;
    }
    const slot = slotRef.current;
    const host = slot?.parentElement;
    const canvas = slot?.closest('.geppetto-edit-stage-outer') || slot?.closest('.course-wrapper');
    if (!host || !canvas) return undefined;
    const measure = () => {
      const hostBox = host.getBoundingClientRect();
      const canvasBox = canvas.getBoundingClientRect();
      setStageBleed({
        width: canvasBox.width,
        marginLeft: canvasBox.left - hostBox.left,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [widthMode]);

  const columns = footerColumns(content);
  const socials = footerSocials(content);
  const logoUrl = String(content.logoUrl || '').trim();
  const logoHref = String(content.logoHref || '').trim();
  const about = String(content.about || '').trim();
  const socialTitle = String(content.socialTitle || '').trim();
  const copy = String(content.copyright || '').trim();
  const legal = (Array.isArray(content.legal) ? content.legal : []).filter((link) => String(link?.label || '').trim());
  const contact = [
    ['phone', content.phone, (value) => `tel:${String(value).replace(/[^\d+]/g, '')}`],
    ['email', content.email, (value) => `mailto:${String(value).trim()}`],
    ['address', content.address, () => ''],
  ].filter(([, value]) => String(value || '').trim());
  const vars = {
    '--sf-bg': content.bg || '#0f172a',
    '--sf-text': content.textColor || '#e2e8f0',
    '--sf-muted': content.mutedColor || '#94a3b8',
    '--sf-heading': content.headingColor || '#ffffff',
    '--sf-link': content.linkColor || '#cbd5e1',
    '--sf-hover': content.linkHoverColor || '#ffffff',
    '--sf-social-bg': content.socialBg || '#1e293b',
    '--sf-social': content.socialColor || '#ffffff',
    '--sf-bar': content.barBg || '#020617',
    '--sf-line': content.lineColor || '#334155',
    '--sf-size': `${Number(content.fontSize) || 14}px`,
    '--sf-logo-h': `${Number(content.logoHeight) || 42}px`,
  };
  const classes = [
    'madri-site-footer',
    `madri-site-footer--${layout}`,
    widthMode === 'boxed' ? 'madri-site-footer--boxed' : 'madri-site-footer--stage-full',
    content.socialStyle === 'plain' ? 'madri-site-footer--plain' : '',
    content.responsive?.mobile?.centerOnMobile ? 'madri-site-footer--mobile-center' : '',
  ].filter(Boolean).join(' ');
  const logoInner = logoUrl
    ? <img src={logoUrl} alt={content.logoAlt || content.logoText || 'Logo'} />
    : (content.logoText || 'Logo');

  return (
    <div ref={slotRef} className="madri-site-footer-slot" style={{ position: 'relative' }}>
      <style>{SITE_FOOTER_CSS}</style>
      <footer
        className={classes}
        style={{
          ...vars,
          ...(stageBleed ? { width: stageBleed.width, maxWidth: 'none', marginLeft: stageBleed.marginLeft, marginRight: 0 } : null),
        }}
      >
        <div className="madri-site-footer__inner">
          <div
            className="madri-site-footer__main"
            style={layout === 'columns' ? { gridTemplateColumns: footerGridColumns(content) } : undefined}
          >
            <div className="madri-site-footer__brand">
              {logoHref ? (
                <a className="madri-site-footer__logo" href={logoHref} onClick={stop}>{logoInner}</a>
              ) : (
                <span className="madri-site-footer__logo">{logoInner}</span>
              )}
              {about ? <div className="madri-site-footer__about">{about}</div> : null}
              {contact.length > 0 && (
                <ul className="madri-site-footer__contact">
                  {contact.map(([key, value, hrefOf]) => {
                    const href = hrefOf(value);
                    return (
                      <li key={key}>
                        <ContactIcon type={key} />
                        {href ? <a href={href} onClick={stop}>{value}</a> : <span>{value}</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="madri-site-footer__menus">
              {columns.map((col, index) => (
                <div className="madri-site-footer__col" key={`${col.title || 'col'}-${index}`}>
                  {String(col.title || '').trim() ? <div className="madri-site-footer__title">{col.title}</div> : null}
                  <ul className="madri-site-footer__list">
                    {(Array.isArray(col.links) ? col.links : []).filter((link) => String(link?.label || '').trim()).map((link, linkIndex) => (
                      <li key={`${link.label}-${linkIndex}`}>
                        <a href={link.href || '#'} onClick={stop}>{link.label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {socials.length > 0 && (
              <div className="madri-site-footer__social">
                {socialTitle && layout !== 'compact' ? <div className="madri-site-footer__title">{socialTitle}</div> : null}
                <div className="madri-site-footer__icons">
                  {socials.map((item, index) => (
                    <a key={`${item.network}-${index}`} className="madri-site-footer__icon" href={item.href} aria-label={item.network} onClick={stop} dangerouslySetInnerHTML={{ __html: footerSocialSvg(item.network) }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {(copy || legal.length > 0) && (
          <div className="madri-site-footer__bar">
            <div className="madri-site-footer__bar-inner">
              {copy ? <div className="madri-site-footer__copy">{copy}</div> : null}
              {legal.length > 0 && (
                <ul className="madri-site-footer__legal">
                  {legal.map((link, index) => (
                    <li key={`${link.label}-${index}`}>
                      <a href={link.href || '#'} onClick={stop}>{link.label}</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </footer>
    </div>
  );
}

function ContactIcon({ type }) {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (type === 'phone') {
    return <svg {...common}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8.1 9.5a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" /></svg>;
  }
  if (type === 'email') {
    return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
  }
  return <svg {...common}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></svg>;
}
